<template>
  <div class="aiv-time-select" ref="timeSelectRoot">
    <el-input
      v-model="inputValue"
      :placeholder="placeholder"
      :disabled="disabled"
      @focus="handleFocus"
      @blur="handleBlur"
      @input="handleInput"
      @keydown.enter="handleEnter"
      style="width: 100%"
    >
      <span
        slot="suffix"
        class="el-input__suffix-inner"
        tabindex="0"
        @keydown.enter="toggleDropdown"
        @keydown.space="toggleDropdown"
        @click.stop="toggleDropdown"
      >
        <i class="el-input__icon el-icon-time"></i>
      </span>
    </el-input>

    <div
      v-if="dropdownOpen"
      class="time-picker-dropdown"
      :style="dropdownStyle"
      @click.stop
    >
      <div class="time-picker-panels">
        <div class="time-panel">
          <div class="panel-header">Hour</div>
          <div class="time-options" ref="hourPanel">
            <div
              v-for="hour in hourOptions"
              :key="hour"
              :class="['time-option', { active: selectedHour === hour }]"
              @click.stop="selectTime('hour', hour)"
            >
              {{ hour }}
            </div>
          </div>
        </div>
        <div class="time-panel">
          <div class="panel-header">Minute</div>
          <div class="time-options" ref="minutePanel">
            <div
              v-for="minute in minuteOptions"
              :key="minute"
              :class="['time-option', { active: selectedMinute === minute }]"
              @click.stop="selectTime('minute', minute)"
            >
              {{ minute.toString().padStart(2, "0") }}
            </div>
          </div>
        </div>
        <div class="time-panel">
          <div class="panel-header">AM/PM</div>
          <div class="time-options" ref="periodPanel">
            <div
              :class="['time-option', { active: selectedPeriod === 'AM' }]"
              @click.stop="selectTime('period', 'AM')"
            >
              AM
            </div>
            <div
              :class="['time-option', { active: selectedPeriod === 'PM' }]"
              @click.stop="selectTime('period', 'PM')"
            >
              PM
            </div>
          </div>
        </div>
      </div>
      <div class="time-picker-footer">
        <el-button size="mini" @click.stop="clearTime">Clear</el-button>
        <el-button size="mini" type="primary" @click.stop="handleOkClick"
          >OK</el-button
        >
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AIVTimeSelect",
  props: {
    value: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "hh:mm am/pm",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    minuteInterval: {
      type: Number,
      default: 5,
      validator: (value) => [5, 15, 30].includes(value),
    },
    allowExactMinutes: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dropdownOpen: false,
      dropdownCoords: { top: 0, left: 0, width: 0 },
      selectedHour: 1,
      selectedMinute: 0,
      selectedPeriod: "AM",
      hourOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
      inputValue: "",
      isTyping: false,
    };
  },
  computed: {
    minuteOptions() {
      const options = [];
      for (let i = 0; i < 60; i += this.minuteInterval) {
        options.push(i);
      }
      return options;
    },
    displayValue() {
      if (!this.value) return "";

      const [hours, minutes] = this.value.split(":").map(Number);
      let displayHour = hours;
      let period = "AM";

      if (hours === 0) {
        displayHour = 12;
      } else if (hours === 12) {
        period = "PM";
      } else if (hours > 12) {
        displayHour = hours - 12;
        period = "PM";
      }

      return `${displayHour}:${String(minutes).padStart(2, "0")} ${period}`;
    },
    dropdownStyle() {
      return {
        position: "fixed",
        top: this.dropdownCoords.top + "px",
        left: this.dropdownCoords.left + "px",
        width: this.dropdownCoords.width + "px",
        zIndex: 3000,
      };
    },
  },
  watch: {
    value: {
      handler(newVal) {
        this.parseTimeValue(newVal);
        if (!this.isTyping) {
          this.inputValue = newVal ? this.displayValue : "";
        }
      },
      immediate: true,
    },
  },
  mounted() {
    document.addEventListener("click", this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleOutsideClick);
    window.removeEventListener("scroll", this.adjustDropdownPosition, true);
    window.removeEventListener("resize", this.adjustDropdownPosition);
  },
  methods: {
    parseTimeValue(timeValue) {
      if (!timeValue) {
        this.selectedHour = 1;
        this.selectedMinute = 0;
        this.selectedPeriod = "AM";
        return;
      }

      const [hours, minutes] = timeValue.split(":").map(Number);

      if (hours === 0) {
        this.selectedHour = 12;
        this.selectedPeriod = "AM";
      } else if (hours === 12) {
        this.selectedHour = 12;
        this.selectedPeriod = "PM";
      } else if (hours > 12) {
        this.selectedHour = hours - 12;
        this.selectedPeriod = "PM";
      } else {
        this.selectedHour = hours;
        this.selectedPeriod = "AM";
      }

      this.selectedMinute = minutes;
    },
    toggleDropdown() {
      if (this.disabled) return;
      this.dropdownOpen = !this.dropdownOpen;
      if (this.dropdownOpen) {
        this.openDropdown();
      } else {
        this.closeDropdown();
      }
    },
    openDropdown() {
      this.dropdownOpen = true;
      this.$nextTick(() => {
        this.adjustDropdownPosition();
        this.scrollToSelectedValues();
        window.addEventListener("scroll", this.adjustDropdownPosition, true);
        window.addEventListener("resize", this.adjustDropdownPosition);
      });
    },
    closeDropdown() {
      // If no value is set but we have selected values (default 9:00 AM), emit the value
      if (
        !this.value &&
        (this.selectedHour || this.selectedMinute || this.selectedPeriod)
      ) {
        this.updateValue();
      }
      this.dropdownOpen = false;
      window.removeEventListener("scroll", this.adjustDropdownPosition, true);
      window.removeEventListener("resize", this.adjustDropdownPosition);
    },
    adjustDropdownPosition() {
      const trigger = this.$refs.timeSelectRoot.getBoundingClientRect();
      const dropdownHeight = 320; // Approximate height of dropdown

      const spaceBelow = window.innerHeight - trigger.bottom;
      const spaceAbove = trigger.top;
      const openUp = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;

      if (openUp) {
        this.dropdownCoords.top = trigger.top - dropdownHeight - 5;
      } else {
        this.dropdownCoords.top = trigger.bottom + 5;
      }

      this.dropdownCoords.left = trigger.left;
      this.dropdownCoords.width = Math.max(trigger.width, 320);
    },
    scrollToSelectedValues() {
      // Scroll hour panel
      const hourPanel = this.$refs.hourPanel;
      if (hourPanel) {
        const activeHour = hourPanel.querySelector(".time-option.active");
        if (activeHour) {
          const containerHeight = hourPanel.clientHeight;
          const optionTop = activeHour.offsetTop;
          const optionHeight = activeHour.clientHeight;
          hourPanel.scrollTop =
            optionTop - containerHeight / 2 + optionHeight / 2;
        }
      }

      // Scroll minute panel
      const minutePanel = this.$refs.minutePanel;
      if (minutePanel) {
        const activeMinute = minutePanel.querySelector(".time-option.active");
        if (activeMinute) {
          const containerHeight = minutePanel.clientHeight;
          const optionTop = activeMinute.offsetTop;
          const optionHeight = activeMinute.clientHeight;
          minutePanel.scrollTop =
            optionTop - containerHeight / 2 + optionHeight / 2;
        }
      }
    },
    selectTime(type, value) {
      if (type === "hour") {
        this.selectedHour = value;
      } else if (type === "minute") {
        this.selectedMinute = value;
      } else if (type === "period") {
        this.selectedPeriod = value;
      }

      this.updateValue();
    },
    updateValue() {
      let hours = this.selectedHour;

      // Convert to 24-hour format
      if (this.selectedPeriod === "AM") {
        hours = this.selectedHour === 12 ? 0 : this.selectedHour;
      } else {
        hours = this.selectedHour === 12 ? 12 : this.selectedHour + 12;
      }

      const formattedTime = `${String(hours).padStart(2, "0")}:${String(this.selectedMinute).padStart(2, "0")}`;
      this.$emit("input", formattedTime);
    },
    clearTime() {
      this.$emit("input", "");
      this.selectedHour = 1;
      this.selectedMinute = 0;
      this.selectedPeriod = "AM";
      this.closeDropdown();
    },
    handleOkClick() {
      // Update the value first if it hasn't been set yet
      if (!this.value) {
        this.updateValue();
        // Wait for the value to propagate before emitting ok-clicked
        this.$nextTick(() => {
          this.$emit("ok-clicked");
          this.closeDropdown();
        });
      } else {
        this.$emit("ok-clicked");
        this.closeDropdown();
      }
    },
    handleOutsideClick(event) {
      if (!this.$el.contains(event.target)) {
        this.dropdownOpen = false;
      }
    },
    handleFocus() {
      this.isTyping = true;
    },
    handleBlur() {
      this.isTyping = false;
      this.parseManualInput();
    },
    handleInput() {
      this.isTyping = true;
    },
    handleEnter() {
      this.parseManualInput();
      this.$refs.timeSelectRoot.querySelector("input").blur();
    },
    parseManualInput() {
      const input = this.inputValue.trim();
      if (!input) {
        this.$emit("input", "");
        return;
      }

      // Try to parse various time formats
      const timeRegex = /^(\d{1,2}):?(\d{0,2})\s*(am|pm|a|p)?$/i;
      const match = input.match(timeRegex);

      if (match) {
        let hour = parseInt(match[1]);
        let minute = match[2] ? parseInt(match[2]) : 0;
        let period = match[3] ? match[3].toLowerCase() : "";

        // Validate hour
        if (hour < 1 || hour > 12) {
          this.inputValue = this.displayValue;
          return;
        }

        // Validate and handle minutes
        if (this.allowExactMinutes) {
          // Allow exact minutes when typed manually
          if (minute < 0 || minute > 59) {
            this.inputValue = this.displayValue;
            return;
          }
        } else {
          // Round minute to nearest interval
          minute = Math.round(minute / this.minuteInterval) * this.minuteInterval;
          if (minute >= 60) minute = 60 - this.minuteInterval;
        }

        // Default to AM if no period specified and hour is 1-11
        if (!period && hour !== 12) {
          period = "am";
        } else if (!period && hour === 12) {
          period = "pm";
        }

        // Convert single character to full
        if (period === "a") period = "am";
        if (period === "p") period = "pm";

        // Update selected values
        this.selectedHour = hour;
        this.selectedMinute = minute;
        this.selectedPeriod = period.toUpperCase();

        this.updateValue();
        this.inputValue = this.displayValue;
      } else {
        // Invalid format, revert to previous value
        this.inputValue = this.displayValue;
      }
    },
  },
};
</script>

<style scoped>
.aiv-time-select {
  position: relative;
  width: 100%;
}

/* Match date picker input styling */
:deep(.el-input__inner) {
  border-radius: 6px !important;
  color: #e0e0e0 !important;
  height: 40px !important;
  font-size: 14px !important;
  font-weight: normal !important;
}

:deep(.el-input__inner:focus) {
  border-color: #ffffff !important;
  box-shadow: none !important;
  background-color: #656565 !important;
  color: #e0e0e0 !important;
}

:deep(.el-input__inner::placeholder) {
  color: #e0e0e0 !important;
  font-size: 14px !important;
}

:deep(.el-input__suffix) {
  color: #000000 !important;
}

:deep(.el-input__suffix-inner:focus) {
  outline: 1px solid #ffffff !important;
  outline-offset: -1px !important;
  border-radius: 2px;
}

.time-picker-dropdown {
  background: #656565;
  border: 1px solid #7a7a7a;
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
  min-width: 320px;
}

.time-picker-panels {
  display: flex;
  padding: 10px;
  gap: 20px;
}

.time-panel {
  flex: 1;
  min-width: 0;
}

.panel-header {
  text-align: center;
  padding: 5px;
  color: #fff;
  font-weight: 600;
  border-bottom: 1px solid #7a7a7a;
  margin-bottom: 5px;
}

.time-options {
  max-height: 200px;
  overflow-y: auto;
}

.time-option {
  padding: 5px 10px;
  text-align: center;
  cursor: pointer;
  color: #e0e0e0;
  transition: all 0.2s;
}

.time-option:hover {
  background: #757575;
  color: #fff;
}

.time-option.active {
  background: #409eff;
  color: #fff;
}

.time-picker-footer {
  padding: 10px;
  border-top: 1px solid #7a7a7a;
  text-align: right;
  display: flex;
  justify-content: space-between;
}

/* Custom scrollbar for time options */
.time-options::-webkit-scrollbar {
  width: 6px;
}

.time-options::-webkit-scrollbar-track {
  background: #555555;
}

.time-options::-webkit-scrollbar-thumb {
  background: #ffffff;
  border-radius: 3px;
}

.time-options::-webkit-scrollbar-thumb:hover {
  background: #ffffff;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .time-picker-dropdown {
    left: 50% !important;
    transform: translateX(-50%) !important;
    width: 95vw !important;
    max-width: 340px !important;
    min-width: unset !important;
  }

  .time-picker-panels {
    gap: 10px;
    padding: 8px;
  }

  .time-panel {
    flex: 1;
    min-width: 0;
  }

  .panel-header {
    font-size: 14px;
    padding: 8px 4px;
  }

  .time-option {
    padding: 10px 5px;
    font-size: 16px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .time-options {
    max-height: 220px;
  }

  .time-picker-footer {
    padding: 8px;
  }

  .time-picker-footer .el-button {
    font-size: 16px;
    padding: 10px 16px;
    min-height: 40px;
  }

  /* Increase touch target for input */
  :deep(.el-input__inner) {
    min-height: 44px !important;
    height: 44px !important;
    font-size: 16px !important;
  }
}
</style>
